import { bridgeFrontendUrl, ENV } from '~/constants/config/env';

/**
 * SSO provider keys that the bridge knows how to forward. Mirrors the keys
 * used by `SSO_PROVIDERS` in `login-page.tsx`.
 */
export type SsoBridgeProvider = 'google' | 'microsoft';

const BRIDGE_PROVIDERS: ReadonlySet<SsoBridgeProvider> = new Set(['google', 'microsoft']);

function isBridgeProvider(value: string | null | undefined): value is SsoBridgeProvider {
  return value != null && BRIDGE_PROVIDERS.has(value as SsoBridgeProvider);
}

const STORAGE_KEY = 'sso_bridge_return';
const QUERY_BRIDGE_FOR = 'bridgeFor';
const QUERY_BRIDGE_PROVIDER = 'bridgeProvider';

/**
 * Strict allowlist for bridge return URLs. Only same-machine localhost loopback
 * origins are accepted — anything else would turn the deployed FE into an
 * open-redirect vector that smuggles single-use auth codes off-domain.
 */
const ALLOWED_BRIDGE_ORIGIN = /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/;

function isLocalhostHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1';
}

function safeOrigin(raw: string | undefined | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return ALLOWED_BRIDGE_ORIGIN.test(url.origin) ? url.origin : null;
  } catch {
    return null;
  }
}

/**
 * True when SSO must bounce through a remote frontend before initiating
 * OAuth. Triggered only in `vite dev` on a localhost host pointed at a
 * non-localhost `VITE_API_BASE_URL` — the configuration where the remote
 * identity service rejects `redirect_origin=http://localhost:3000` and
 * falls back to its own `FRONTEND_URL`.
 */
export function shouldBridgeSso(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  if (!isLocalhostHost(window.location.hostname)) return false;

  const apiBase = ENV.VITE_API_BASE_URL;
  if (!apiBase) return false;

  let apiOrigin: string;
  try {
    apiOrigin = new URL(apiBase).origin;
  } catch {
    return false;
  }

  // Local backend (e.g. http://localhost:3001) — no bridge needed; the local
  // identity service already accepts localhost in its ALLOWED_ORIGINS.
  try {
    if (isLocalhostHost(new URL(apiOrigin).hostname)) return false;
  } catch {
    return false;
  }

  return apiOrigin !== window.location.origin;
}

/**
 * Builds the URL that hands SSO initiation to the remote frontend. Returns
 * null if the provider isn't bridge-able or the return origin fails the
 * localhost-only check. The remote FE's `/login` reads the params, stashes
 * the return URL on its own origin, and auto-triggers the requested
 * provider with its own (allowlisted) origin as `redirect_origin`.
 *
 * Targets the deployed **front-end** origin (`bridgeFrontendUrl`), NOT
 * `VITE_API_BASE_URL` — `/login` is an SPA route, so pointing it at the
 * identity-service host returns `Cannot GET /login` (404).
 */
export function buildBridgeUrl(provider: string, returnTo: string): string | null {
  if (!isBridgeProvider(provider)) return null;
  if (!safeOrigin(returnTo)) return null;
  let feOrigin: string;
  try {
    feOrigin = new URL(bridgeFrontendUrl).origin;
  } catch {
    return null;
  }
  const params = new URLSearchParams({
    [QUERY_BRIDGE_FOR]: returnTo,
    [QUERY_BRIDGE_PROVIDER]: provider,
  });
  return `${feOrigin}/login?${params.toString()}`;
}

/**
 * Validates the raw bridge query strings. Returns null when absent or when
 * either value fails its respective check (origin must be localhost loopback,
 * provider must be a known bridge-able key).
 */
export function readBridgeParams(
  rawReturn: string | undefined | null,
  rawProvider: string | undefined | null,
): { returnTo: string; provider: SsoBridgeProvider } | null {
  const returnTo = safeOrigin(rawReturn);
  if (!returnTo) return null;
  if (!isBridgeProvider(rawProvider)) return null;
  return { returnTo, provider: rawProvider };
}

/**
 * Persists the bridge return URL across the OAuth round-trip on the remote
 * FE's origin. Always clears any prior entry first so a leftover stash from
 * an abandoned flow cannot redirect a fresh non-bridge OAuth callback to
 * localhost.
 */
export function stashBridgeReturn(returnTo: string): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    const safe = safeOrigin(returnTo);
    if (safe) sessionStorage.setItem(STORAGE_KEY, safe);
  } catch {
    // sessionStorage unavailable — bridge will gracefully no-op and the
    // user will land on the remote FE as before.
  }
}

/**
 * Reads + clears the stashed bridge return URL. Single-shot to guarantee
 * the bridge can never fire twice for the same stash. Returns null when
 * absent, unreadable, or no longer matching the localhost allowlist.
 */
export function consumeBridgeReturn(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  return safeOrigin(raw);
}

/**
 * Drops any abandoned bridge stash without consuming it. Called when /login
 * loads without bridge params, so a prior incomplete flow cannot redirect a
 * fresh non-bridge OAuth callback to localhost.
 */
export function clearBridgeReturn(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}
